<script setup>
import {onMounted, ref} from 'vue';
import {Dialog, DialogPanel, DialogTitle, TransitionRoot} from '@headlessui/vue';
import {useI18n} from 'vue-i18n';
import {
  abilityToLossOptions,
  adequacyTestItems,
  clientEsg,
  clientInvestmentGoals,
  clientTypes,
  investmentHorizonOptions,
  investmentPreferences,
  riskToleranceCorrectionOptions,
} from '@/utils/targetMarketOptions';
import DocumentTemplateService from '@/services/DocumentTemplateService';

/**
 * IMA-517: nastavení dluhopisu v aplikaci (menu Dluhopisy → detail → Nastavení v aplikaci).
 *
 * Údaje z ESO se tu neupravují. Poplatek se zatím jen eviduje (nepočítá se): typ, měna a pásma
 * podle výše nákupu. Validaci pásem (překryv, procenta do 100) dělá i BE (BondSettingsSaveRequest).
 */
const {t, te} = useI18n();

const props = defineProps({
  bond: {type: Object, required: true},
  intermediaries: {type: Array, default: () => []},
});
const emit = defineEmits(['confirm', 'cancel']);
const isOpen = ref(true);

// kopie hodnot, aby se změny v detailu neprojevily před uložením
const settings = ref({
  is_active: !!props.bond.is_active,
  sale_from: props.bond.sale_from ?? '',
  sale_to: props.bond.sale_to ?? '',
  client_description: props.bond.client_description ?? '',
  // IMA-517 fáze 10: šablona „Pokyn k nákupu dluhopisů“; prázdná = nákup bez pokynu
  purchase_order_template_id: props.bond.purchase_order_template_id ?? null,
  // min./max. počet kusů na jeden nákup (ESO je zatím neposílá – E12); prázdné = min. 1 ks, bez maxima
  min_quantity: props.bond.min_quantity ?? null,
  max_quantity: props.bond.max_quantity ?? null,
  available_for_intermediaries: [...(props.bond.available_for_intermediaries ?? [])],
  // rizikový profil jako u strategie: číslo, klient projde se stejným nebo vyšším (TargetMarketMatcher); prázdné = bez omezení
  risk_profile: props.bond.risk_profile ?? '',
  client_type: [...(props.bond.client_type ?? [])],
  adequacy_test: [...(props.bond.adequacy_test ?? [])],
  ability_to_bear_loss: [...(props.bond.ability_to_bear_loss ?? [])],
  risk_tolerance_correction: [...(props.bond.risk_tolerance_correction ?? [])],
  investment_horizon: [...(props.bond.investment_horizon ?? [])],
  client_investment_goals: [...(props.bond.client_investment_goals ?? [])],
  investment_preferences: [...(props.bond.investment_preferences ?? [])],
  client_esg: [...(props.bond.client_esg ?? [])],
});

/** Aktivní šablony dokumentů pro výběr šablony pokynu k nákupu. */
const documentTemplates = ref([]);
onMounted(() => {
  DocumentTemplateService.getAllActive().then(response => {
    documentTemplates.value = response;
  });
});

// poplatek je nepovinný – bez zapnutí se posílá null
const hasFee = ref(!!props.bond.fee);
const fee = ref(props.bond.fee
    ? {type: props.bond.fee.type, currency: props.bond.fee.currency ?? props.bond.currency ?? 'CZK', tiers: props.bond.fee.tiers.map(tier => ({...tier}))}
    : {type: 'PERCENT', currency: props.bond.currency ?? 'CZK', tiers: [{amount_from: 0, amount_to: null, value: 0}]});

function addTier() {
  // nové pásmo navazuje na horní hranici posledního pásma
  const last = fee.value.tiers[fee.value.tiers.length - 1];
  fee.value.tiers.push({amount_from: last?.amount_to ?? 0, amount_to: null, value: 0});
}

function removeTier(index) {
  fee.value.tiers.splice(index, 1);
}

/** Prázdné pole multiselectu = bez omezení, BE ho ukládá jako null (stejně jako u strategií). */
const emptyToNull = values => (Array.isArray(values) && values.length === 0 ? null : values);

const closeModalWith = (value) => {
  setTimeout(() => {
    if (value === 'confirm') {
      const data = {...settings.value};
      data.risk_profile = data.risk_profile === '' || data.risk_profile === null ? null : Number(data.risk_profile);
      // prázdné pole (v-model.number vrací '') = bez omezení
      for (const key of ['min_quantity', 'max_quantity']) {
        data[key] = data[key] === '' || data[key] === null ? null : Number(data[key]);
      }
      for (const key of ['available_for_intermediaries', 'client_type', 'adequacy_test', 'ability_to_bear_loss', 'risk_tolerance_correction', 'investment_horizon', 'client_investment_goals', 'investment_preferences', 'client_esg']) {
        data[key] = emptyToNull(data[key]);
      }
      data.fee = hasFee.value ? {
        type: fee.value.type,
        currency: fee.value.currency || null,
        tiers: fee.value.tiers.map(tier => ({
          amount_from: Number(tier.amount_from),
          amount_to: tier.amount_to === null || tier.amount_to === '' ? null : Number(tier.amount_to),
          value: Number(tier.value),
        })),
      } : null;
      emit('confirm', data);
    } else {
      emit('cancel');
    }
    isOpen.value = false;
  }, 300);
};

const optionLabel = value => (te('createUpdateStrategyModal.' + value) ? t('createUpdateStrategyModal.' + value) : value);
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="closeModalWith('cancel')" class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle as="h3" class="modal-title">{{ t('bondSettingsModal.title') }}</DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <form @submit.prevent="closeModalWith('confirm')">
              <div class="modal-body">
                <div class="row mb-3">
                  <div class="col-12">
                    <div class="form-check">
                      <label for="bondIsActive" class="form-label">{{ t('bondSettingsModal.is_active') }}</label>
                      <input type="checkbox" v-model="settings.is_active" id="bondIsActive" class="form-check-input"/>
                    </div>
                    <small class="text-muted">{{ t('bondSettingsModal.is_active_hint') }}</small>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-6 form-group">
                    <label for="bondSaleFrom" class="form-label">{{ t('bondSettingsModal.sale_from') }}</label>
                    <input v-model="settings.sale_from" id="bondSaleFrom" type="date" class="form-control"/>
                  </div>
                  <div class="col-6 form-group">
                    <label for="bondSaleTo" class="form-label">{{ t('bondSettingsModal.sale_to') }}</label>
                    <input v-model="settings.sale_to" id="bondSaleTo" type="date" class="form-control" :min="settings.sale_from || null"/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="bondDescription" class="form-label">{{ t('bondSettingsModal.client_description') }}</label>
                    <textarea v-model="settings.client_description" id="bondDescription" class="form-control" rows="2"></textarea>
                  </div>
                </div>
                <div class="row mb-1">
                  <div class="col-6 form-group">
                    <label for="bondMinQuantity" class="form-label">{{ t('bondSettingsModal.min_quantity') }}</label>
                    <input v-model.number="settings.min_quantity" id="bondMinQuantity" type="number" min="1" step="1" class="form-control"/>
                  </div>
                  <div class="col-6 form-group">
                    <label for="bondMaxQuantity" class="form-label">{{ t('bondSettingsModal.max_quantity') }}</label>
                    <!-- maximum nesmí být menší než minimum (kontroluje i BE) -->
                    <input v-model.number="settings.max_quantity" id="bondMaxQuantity" type="number" :min="settings.min_quantity || 1" step="1" class="form-control"/>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12"><div class="form-text">{{ t('bondSettingsModal.quantity_hint') }}</div></div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="bondPurchaseOrderTemplate" class="form-label">{{ t('bondSettingsModal.purchase_order_template') }}</label>
                    <select v-model="settings.purchase_order_template_id" id="bondPurchaseOrderTemplate" class="form-select">
                      <option :value="null">{{ t('bondSettingsModal.purchase_order_template_none') }}</option>
                      <option v-for="template of documentTemplates" :key="template.id" :value="template.id">{{ template.name }}</option>
                    </select>
                    <div class="form-text">{{ t('bondSettingsModal.purchase_order_template_hint') }}</div>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="bondIntermediaries" class="form-label">{{ t('createUpdateStrategyModal.intermediaries_can_sell') }}</label>
                    <select v-model="settings.available_for_intermediaries" id="bondIntermediaries" class="form-select" multiple>
                      <option v-for="intermediary in intermediaries" :key="intermediary.id" :value="intermediary.id">{{ intermediary.intermediary_name }}</option>
                    </select>
                    <small class="text-muted">{{ t('bondSettingsModal.empty_means_all') }}</small>
                  </div>
                </div>

                <h5 class="mt-4 mb-3">{{ t('bondSettingsModal.target_market') }}</h5>
                <div class="row mb-3">
                  <div class="col-12 col-md-6 form-group mb-3">
                    <label for="bondRiskProfile" class="form-label">{{ t('bondsDetail.risk_profile') }}</label>
                    <input v-model="settings.risk_profile" id="bondRiskProfile" class="form-control" type="number" step="1" min="1" max="50"/>
                  </div>
                  <div class="col-12 col-md-6 form-group mb-3">
                    <label for="bondClientType" class="form-label">{{ t('createUpdateStrategyModal.client_type') }}</label>
                    <select v-model="settings.client_type" id="bondClientType" class="form-select" multiple>
                      <option v-for="item in clientTypes" :key="item" :value="item">{{ optionLabel(item) }}</option>
                    </select>
                  </div>
                  <div class="col-12 col-md-6 form-group mb-3">
                    <label for="bondAdequacy" class="form-label">{{ t('createUpdateStrategyModal.adequacy_test') }}</label>
                    <select v-model="settings.adequacy_test" id="bondAdequacy" class="form-select" multiple>
                      <option v-for="item in adequacyTestItems" :key="item" :value="item">{{ optionLabel(item) }}</option>
                    </select>
                  </div>
                  <div class="col-12 col-md-6 form-group mb-3">
                    <label for="bondLoss" class="form-label">{{ t('createUpdateStrategyModal.ability_to_bear_loss') }}</label>
                    <select v-model="settings.ability_to_bear_loss" id="bondLoss" class="form-select" multiple>
                      <option v-for="item in abilityToLossOptions" :key="item" :value="item">{{ optionLabel(item) }}</option>
                    </select>
                  </div>
                  <div class="col-12 col-md-6 form-group mb-3">
                    <label for="bondRisk" class="form-label">{{ t('createUpdateStrategyModal.risk_tolerance_correction') }}</label>
                    <select v-model="settings.risk_tolerance_correction" id="bondRisk" class="form-select" multiple>
                      <option v-for="item in riskToleranceCorrectionOptions" :key="item.key" :value="item.key">{{ optionLabel(item.value) }}</option>
                    </select>
                  </div>
                  <div class="col-12 col-md-6 form-group mb-3">
                    <label for="bondHorizon" class="form-label">{{ t('createUpdateStrategyModal.investment_horizon') }}</label>
                    <select v-model="settings.investment_horizon" id="bondHorizon" class="form-select" multiple>
                      <option v-for="item in investmentHorizonOptions" :key="item" :value="item">{{ typeof item === 'number' ? item : optionLabel(item) }}</option>
                    </select>
                  </div>
                  <div class="col-12 col-md-6 form-group mb-3">
                    <label for="bondGoals" class="form-label">{{ t('createUpdateStrategyModal.client_investment_goals') }}</label>
                    <select v-model="settings.client_investment_goals" id="bondGoals" class="form-select" multiple>
                      <option v-for="item in clientInvestmentGoals" :key="item" :value="item">{{ optionLabel(item) }}</option>
                    </select>
                  </div>
                  <div class="col-12 col-md-6 form-group mb-3">
                    <label for="bondPreferences" class="form-label">{{ t('createUpdateStrategyModal.investment_preferences') }}</label>
                    <select v-model="settings.investment_preferences" id="bondPreferences" class="form-select" multiple>
                      <option v-for="item in investmentPreferences" :key="item" :value="item">{{ optionLabel(item) }}</option>
                    </select>
                  </div>
                  <div class="col-12 col-md-6 form-group mb-3">
                    <label for="bondEsg" class="form-label">{{ t('createUpdateStrategyModal.client_esg') }}</label>
                    <select v-model="settings.client_esg" id="bondEsg" class="form-select" multiple>
                      <option v-for="item in clientEsg" :key="item" :value="item">{{ optionLabel(item) }}</option>
                    </select>
                  </div>
                </div>

                <h5 class="mt-4 mb-2">{{ t('bondSettingsModal.fee') }}</h5>
                <p class="small text-muted">{{ t('bondSettingsModal.fee_hint') }}</p>
                <div class="form-check form-switch mb-3">
                  <input class="form-check-input" type="checkbox" role="switch" id="bondHasFee" v-model="hasFee"/>
                  <label class="form-check-label" for="bondHasFee">{{ t('bondSettingsModal.has_fee') }}</label>
                </div>
                <template v-if="hasFee">
                  <div class="row mb-3">
                    <div class="col-6 form-group">
                      <label for="bondFeeType" class="form-label">{{ t('bondSettingsModal.fee_type') }}</label>
                      <select v-model="fee.type" id="bondFeeType" class="form-select">
                        <option value="PERCENT">{{ t('bondSettingsModal.fee_type_percent') }}</option>
                        <option value="FIXED">{{ t('bondSettingsModal.fee_type_fixed') }}</option>
                      </select>
                    </div>
                    <div class="col-6 form-group">
                      <label for="bondFeeCurrency" class="form-label">{{ t('bondSettingsModal.currency') }}</label>
                      <select v-model="fee.currency" id="bondFeeCurrency" class="form-select" :required="fee.type === 'FIXED'">
                        <option value="CZK">CZK</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </div>
                  </div>
                  <div v-for="(tier, index) in fee.tiers" :key="index" class="row mb-2 align-items-end">
                    <div class="col-4 form-group">
                      <label :for="'tierFrom' + index" class="form-label">{{ t('bondSettingsModal.amount_from') }}</label>
                      <input v-model="tier.amount_from" :id="'tierFrom' + index" type="number" min="0" step="0.01" class="form-control" required/>
                    </div>
                    <div class="col-4 form-group">
                      <label :for="'tierTo' + index" class="form-label">{{ t('bondSettingsModal.amount_to') }}</label>
                      <input v-model="tier.amount_to" :id="'tierTo' + index" type="number" min="0" step="0.01" class="form-control" :placeholder="t('bondSettingsModal.unlimited')"/>
                    </div>
                    <div class="col-3 form-group">
                      <label :for="'tierValue' + index" class="form-label">{{ fee.type === 'PERCENT' ? t('bondSettingsModal.value_percent') : t('bondSettingsModal.value_fixed') }}</label>
                      <input v-model="tier.value" :id="'tierValue' + index" type="number" min="0" :max="fee.type === 'PERCENT' ? 100 : null" step="0.01" class="form-control" required/>
                    </div>
                    <div class="col-1">
                      <button v-if="fee.tiers.length > 1" type="button" class="btn btn-link" :aria-label="t('bondSettingsModal.remove_tier')" @click="removeTier(index)"><i class="mdi mdi-delete"/></button>
                    </div>
                  </div>
                  <button type="button" class="btn btn-link px-0" @click="addTier()"><i class="mdi mdi-plus"/> {{ t('bondSettingsModal.add_tier') }}</button>
                </template>
              </div>

              <div class="modal-footer">
                <button type="button" class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('createUpdateStrategyModal.cancel') }}</button>
                <button class="btn btn-primary btn-md" type="submit">{{ t('createUpdateStrategyModal.save') }}</button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
